import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";
import Tippy from "@tippyjs/react";
import moment from "moment";
import { ToggleOff, ToggleOn } from "@mui/icons-material";

import { alertDevicesByAlertId, alertsByCompanyID } from "graphql/queries";
import { deleteAlert, deleteAlertDevice, updateAlert } from "graphql/mutations";
import { customGetAlert, getCompanyName } from "graphql/custom-queries";
import usePermissions from "hooks/usePermission";
import { globalStrings } from "config/translations/es";
import { AlertCreateForm, AlertUpdateForm } from ".";
import { LayoutContext } from "layout/context/LayoutContext";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";

const Alerts = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "alert:create-config",
    "alert:update-config",
    "alert:delete-config",
  ]);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertDetails, setAlertDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    alertId: null,
    alertName: null,
  });
  const [modalAlert, setModalAlert] = useState({
    visible: false,
    alertId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      alerts.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        severity: globalStrings.severity.options[item.severity],
        actions: (
          <>
            <Button
              className="px-1"
              children={
                alertDetails?.id === item.id
                  ? strings.actions.hide
                  : strings.actions.show
              }
              variation="link"
              size="small"
              onClick={() => {
                alertDetails?.id === item.id
                  ? setAlertDetails(null)
                  : fetchAlert(item.id);
              }}
            />
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.actions.edit}
                variation="link"
                size="small"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <Button
                className="px-1"
                children={strings.actions.delete}
                variation="link"
                size="small"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    alertId: item.id,
                    alertName: item.name,
                  });
                }}
              />
            )}
            {canUpdate && (
              <>
                {!!item.enabled ? (
                  <Tippy content={strings.actions.deactivate}>
                    <ToggleOn
                      onClick={() => alertToggle(item.id, !item.enabled)}
                      style={styles.iconAction}
                      color="success"
                    ></ToggleOn>
                  </Tippy>
                ) : (
                  <Tippy content={strings.actions.activate}>
                    <ToggleOff
                      onClick={() => alertToggle(item.id, !item.enabled)}
                      style={styles.iconAction}
                      color="error"
                    ></ToggleOff>
                  </Tippy>
                )}
              </>
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alerts, alertDetails?.id]
  );

  const onNext = () => {
    fetchAlerts(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchAlerts(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchAlerts(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalAlert({ visible: true, action, alertId: id });
  const onSuccessAction = () => {
    setModalAlert({ visible: false });
    fetchAlerts();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByAlerts = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setAlerts((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };

  async function fetchAlerts(token, isNext = true, keywords) {
    const nameOperation = alertsByCompanyID;
    const nameOperationStr = "alertsByCompanyID";
    try {
      setAlertsLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const alertsData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      const alertsItems = alertsData.data[nameOperationStr].items;
      setAlerts(alertsItems);
      getNamesByAlerts(alertsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: alertsData.data[nameOperationStr].nextToken,
        };
      });

      setAlertsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setAlertsLoading(false);
    } finally {
      setAlertsLoading(false);
    }
  }
  async function fetchAlert(id) {
    try {
      const alertData = await API.graphql(
        graphqlOperation(customGetAlert, {
          id,
        })
      );
      const alertItem = alertData.data.getAlert;
      setAlertDetails({
        ...alertItem,
        devices: alertItem.devices?.items?.map((item) => item.device.name),
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }
  async function alertDelete(id) {
    try {
      await alertDevicesDelete(id);

      const result = await API.graphql(
        graphqlOperation(deleteAlert, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.deleteAlert.id;
      setAlerts(alerts.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        alertId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  async function alertDevicesDelete(alertId) {
    try {
      const results = await API.graphql(
        graphqlOperation(alertDevicesByAlertId, { alertId })
      );
      const alertsDeviceToDelete = results.data.alertDevicesByAlertId.items;
      await Promise.all(
        alertsDeviceToDelete?.map((alertDevice) =>
          API.graphql(
            graphqlOperation(deleteAlertDevice, {
              input: { id: alertDevice.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones", err);
    }
  }

  async function alertToggle(id, value) {
    try {
      await API.graphql(
        graphqlOperation(updateAlert, { input: { id, enabled: value } })
      );
      toast.success(strings.successUpdated);
      setAlerts(
        alerts.map((item) =>
          item.id === id ? { ...item, enabled: value } : item
        )
      );
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "30%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "30%" },
    },
    {
      key: "severity",
      label: strings.table.headers.severity,
      _style: { width: "20%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.actions.addAlert}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {alertsLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!alertDetails?.id} style={styles.collapseCard}>
          <CCard key={alertDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{alertDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {alertDetails?.description}
              </p>
              <div className="text-muted">
                <b>{strings.details.devices}:</b>
                <ul>
                  {alertDetails?.devices?.map((device, index) => (
                    <li key={index}>{device}</li>
                  ))}
                </ul>
              </div>
              <div className="text-muted">
                <b>{strings.details.emailNotifications}:</b>
                <ul>
                  {alertDetails?.emailNotifications?.map((it, index) => (
                    <li key={index}>{it}</li>
                  ))}
                </ul>
              </div>
              <div className="text-muted">
                <b>{strings.details.wspNotifications}:</b>
                <ul>
                  {alertDetails?.wspNotifications?.map((it, index) => (
                    <li key={index}>{it}</li>
                  ))}
                </ul>
              </div>
              <div className="text-muted">
                <b>{strings.details.smsNotifications}:</b>
                <ul>
                  {alertDetails?.smsNotifications?.map((it, index) => (
                    <li key={index}>{it}</li>
                  ))}
                </ul>
              </div>
              <p className="text-muted">
                <b>{strings.details.endSchedule}:</b>{" "}
                {alertDetails?.endSchedule}
              </p>
              <p className="text-muted">
                <b>{strings.details.startSchedule}:</b>{" "}
                {alertDetails?.startSchedule}
              </p>
              <p className="text-muted">
                <b>{strings.details.severity}:</b>{" "}
                {globalStrings.severity.options[alertDetails?.severity]}
              </p>
              <p className="text-muted">
                <b>{strings.details.severityColor}:</b>{" "}
                {alertDetails?.severityColor}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(alertDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <CModal
        alignment="center"
        visible={modalDetails.visible}
        onClose={() => setModalDetails({ visible: false, alertId: null })}
        backdrop="static"
      >
        <CModalBody>
          <b>
            {strings.formatString(strings.modal.confirmDeleteQuestion, {
              name: modalDetails.alertName,
            })}
          </b>
        </CModalBody>
        <CModalFooter>
          <Button
            size="small"
            onClick={() => setModalDetails({ visible: false, alertId: null })}
          >
            {strings.modal.cancelButton}
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={() => alertDelete(modalDetails.alertId)}
          >
            {strings.modal.confirmButton}
          </Button>
        </CModalFooter>
      </CModal>
      {modalAlert.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalAlert.visible}
          onClose={() => setModalAlert({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalAlert.action === "create"
                ? strings.modalAlert.addTitle
                : strings.modalAlert.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalAlert.action === "create" ? (
                <AlertCreateForm onSuccess={onSuccessAction} />
              ) : (
                <AlertUpdateForm
                  alertId={modalAlert.alertId}
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Configuración de Alertas",
    successDeleted: "Alerta eliminada con éxito",
    successUpdated: "Alerta actualizada con éxito",
    actions: {
      addAlert: "Agregar Alerta",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      activate: "Activar",
      deactivate: "Desactivar",
    },
    responses: {
      errorFetch: "Error al consultar alertas",
    },
    table: {
      headers: {
        companyName: "Empresa",
        name: "Nombre",
        description: "Descripción",
        severity: "Severidad",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de alerta",
      devices: "Dispositivos",
      emailNotifications: "Notificaciones por Correo",
      wspNotifications: "Notificaciones por Whatsapp",
      smsNotifications: "Notificaciones por SMS",
      description: "Descripción",
      endSchedule: "Hora de Fin",
      startSchedule: "Hora de Inicio",
      severity: "Severidad",
      severityColor: "Color de Severidad",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la alerta {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalAlert: {
      addTitle: "Agregar Alerta",
      updateTitle: "Actualizar Alerta",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
  iconAction: {
    cursor: "pointer",
    fontSize: "30px",
  },
};

export default Alerts;
